// Desenho esquemático de portas — espelho de backend/services/sales_doors.py (mesmo algoritmo, polegadas, origem canto inferior esquerdo).
// Entry Door: folha simples/dupla + 0–2 sidelights + transom (none / single / each), vidro (lisa / meio / inteiro) e grade.
// Patio Door: 2–4 painéis, Contemporary / Classic / Francesa e grade.
export const ENTRY_STYLES = ['entry_solid', 'entry_half_lite', 'entry_full_lite'];
export const PATIO_STYLES = ['patio_sliding', 'patio_sliding_classic', 'patio_french'];
export const DOOR_MODELS = ['single', 'double'];
export const TRANSOM_MODES = ['none', 'single', 'each'];
export const HANDLE_STYLES = ['lever', 'knob', 'pull', 'gripset'];
export const DOOR_TYPE_IDS = ['ENTRY_DOOR', 'PATIO_DOOR'];
export const isDoorOpening = (type) => DOOR_TYPE_IDS.includes(type);

const n = (v, d = 0) => { const f = Number(v); return f > 0 ? f : d; };

export const emptyDoor = () => ({ model: 'single', sidelights: 0, sidelight_side: 'right', transom: 'none', sidelight_sizes: [], transom_sizes: [], hinge: 'left', handle: 'lever' });
export const transomCount = (door) => (!door || door.transom === 'none' ? 0 : door.transom === 'single' ? 1 : (Number(door.sidelights) || 0) + 1);
// Ajusta as listas de medidas ao nº de sidelights / transoms (mantém o que já foi digitado)
export const normalizeDoor = (door) => {
  const d = { ...emptyDoor(), ...(door || {}) };
  d.sidelights = Math.max(0, Math.min(2, Number(d.sidelights) || 0));
  if (d.transom === 'each' && d.sidelights === 0) d.transom = 'single';
  const fit = (arr, len) => Array.from({ length: len }, (_, i) => ({ width: arr?.[i]?.width ?? '', height: arr?.[i]?.height ?? '' }));
  d.sidelight_sizes = fit(d.sidelight_sizes, d.sidelights);
  d.transom_sizes = fit(d.transom_sizes, transomCount(d));
  return d;
};
export const doorPartsComplete = (door) => [...(door?.sidelight_sizes || []), ...(door?.transom_sizes || [])].every((p) => Number(p.width) > 0 && Number(p.height) > 0);
export const autoPanels = (w) => (Number(w) <= 96 ? 2 : Number(w) <= 144 ? 3 : 4);

// Linhas da grade dentro de um vidro {x,y,w,h}: [x1,y1,x2,y2,kind] kind = 'bar' | 'brass' | 'etch'
export const grilleLines = (g, grille) => {
  if (!grille || grille === 'none') return [];
  const { x: gx, y: gy, w: gw, h: gh } = g;
  if (gw <= 2 || gh <= 2) return [];
  const out = [];
  const kind = grille === 'brass' ? 'brass' : grille.endsWith('vgroove') ? 'etch' : 'bar';
  if (grille === 'colonial' || grille === 'brass') {
    const cols = Math.max(1, Math.round(gw / 8)), rows = Math.max(1, Math.round(gh / 12));
    for (let i = 1; i < cols; i++) { const x = gx + (gw * i) / cols; out.push([x, gy, x, gy + gh, kind]); }
    for (let j = 1; j < rows; j++) { const y = gy + (gh * j) / rows; out.push([gx, y, gx + gw, y, kind]); }
  } else if (grille === 'prairie') {
    const o = Math.min(3.5, gw / 4, gh / 4);
    out.push([gx + o, gy, gx + o, gy + gh, kind], [gx + gw - o, gy, gx + gw - o, gy + gh, kind], [gx, gy + o, gx + gw, gy + o, kind], [gx, gy + gh - o, gx + gw, gy + gh - o, kind]);
  } else if (grille === 'perimeter' || grille === 'geometric_vgroove') {
    const o = Math.min(3.5, gw / 4, gh / 4);
    const x1 = gx + o, y1 = gy + o, x2 = gx + gw - o, y2 = gy + gh - o;
    out.push([x1, y1, x2, y1, kind], [x2, y1, x2, y2, kind], [x2, y2, x1, y2, kind], [x1, y2, x1, y1, kind]);
    if (grille === 'geometric_vgroove') {
      const c = Math.min(4, (x2 - x1) / 3, (y2 - y1) / 3);
      out.push([x1, y1 + c, x1 + c, y1, kind], [x2 - c, y1, x2, y1 + c, kind], [x2, y2 - c, x2 - c, y2, kind], [x1 + c, y2, x1, y2 - c, kind]);
    }
  } else if (grille === 'craftsman') {
    const band = Math.min(gh * 0.25, 12);
    const y = gy + gh - band;
    out.push([gx, y, gx + gw, y, kind]);
    const cols = Math.max(2, Math.round(gw / 6));
    for (let i = 1; i < cols; i++) { const x = gx + (gw * i) / cols; out.push([x, y, x, gy + gh, kind]); }
  } else if (grille === 'diamond' || grille === 'diamond_vgroove') {
    const nc = Math.max(1, Math.round(gw / 8)), nr = Math.max(1, Math.round(gh / 8));
    const dw = gw / nc, dh = gh / nr;
    for (let i = 0; i <= nc; i++) {
      const x0 = gx + i * dw;
      let t = Math.min(nr, (gx + gw - x0) / dw);
      if (t > 0 && i < nc) out.push([x0, gy, x0 + t * dw, gy + t * dh, kind]);
      t = Math.min(nr, (x0 - gx) / dw);
      if (t > 0 && i > 0) out.push([x0, gy, x0 - t * dw, gy + t * dh, kind]);
    }
    for (let j = 1; j < nr; j++) {
      const y0 = gy + j * dh, t = Math.min(nr - j, nc);
      if (t > 0) out.push([gx, y0, gx + t * dw, y0 + t * dh, kind], [gx + gw, y0, gx + gw - t * dw, y0 + t * dh, kind]);
    }
  }
  return out;
};

const slab = (x, y, w, h, style, handleSide, classic = false, handleStyle = 'lever', hinges = false) => {
  const stile = Math.min(4.5, w * 0.18), top = Math.min(4.5, h * 0.08), bottom = Math.min(classic ? 9 : 6, h * 0.15);
  const glass = [], panels = [];
  if (style === 'entry_full_lite' || style.startsWith('patio_')) {
    glass.push({ x: x + stile, y: y + bottom, w: w - 2 * stile, h: h - top - bottom });
  } else if (style === 'entry_half_lite') {
    const mid = y + h * 0.5;
    glass.push({ x: x + stile, y: mid + 2, w: w - 2 * stile, h: h - top - (mid + 2 - y) });
    const pw = (w - 3 * stile) / 2;
    if (pw > 4) panels.push({ x: x + stile, y: y + bottom, w: pw, h: mid - 3 - y - bottom }, { x: x + 2 * stile + pw, y: y + bottom, w: pw, h: mid - 3 - y - bottom });
    else panels.push({ x: x + stile, y: y + bottom, w: w - 2 * stile, h: mid - 3 - y - bottom });
  } else {
    const pw = (w - 3 * stile) / 2, innerH = h - top - bottom, hi = innerH * 0.58, lo = innerH * 0.42 - 3;
    const cols = pw > 4 ? [x + stile, x + 2 * stile + pw] : [x + stile];
    const cw = pw > 4 ? pw : w - 2 * stile;
    cols.forEach((cx) => panels.push({ x: cx, y: y + bottom + innerH - hi, w: cw, h: hi }, { x: cx, y: y + bottom, w: cw, h: lo }));
  }
  const handle = handleSide ? { x: handleSide === 'left' ? x + stile / 2 : x + w - stile / 2, y: y + h * 0.47, style: handleStyle, side: handleSide } : null;
  const hingePts = handleSide && hinges ? [0.12, 0.5, 0.88].map((f) => ({ x: handleSide === 'left' ? x + w : x, y: y + h * f })) : [];
  return { x, y, w, h, glass, panels, handle, hinges: hingePts };
};

const lite = (x, y, w, h, kind) => {
  const s = Math.min(2.5, w * 0.2, h * 0.2), b = kind === 'sidelight' ? Math.min(6, h * 0.15) : s;
  return { x, y, w, h, kind, glass: { x: x + s, y: y + b, w: w - 2 * s, h: h - s - b } };
};

export const entryLayout = (W, H, door, style) => {
  const d = door || {};
  const st = ENTRY_STYLES.includes(style) ? style : 'entry_full_lite';
  const sl = Math.max(0, Math.min(2, Number(d.sidelights) || 0));
  const side = d.sidelight_side || 'right';
  let tr = d.transom || 'none';
  if (tr === 'each' && sl === 0) tr = 'single';
  const slSizes = d.sidelight_sizes || [], trSizes = d.transom_sizes || [];
  let T = 0;
  if (tr !== 'none') {
    T = Math.max(0, ...trSizes.map((s) => n(s.height))) || Math.min(14, H * 0.2);
    T = Math.min(T, H * 0.45);
  }
  const bodyH = H - T;
  let slw = Array.from({ length: sl }, (_, i) => n(slSizes[i]?.width, 12));
  const sum = slw.reduce((a, b) => a + b, 0);
  if (sum > W * 0.7) slw = slw.map((v) => (v * W * 0.7) / sum);
  const doorW = W - slw.reduce((a, b) => a + b, 0);
  const order = sl === 2 ? ['sl0', 'door', 'sl1'] : sl === 1 && side === 'left' ? ['sl0', 'door'] : sl === 1 ? ['door', 'sl0'] : ['door'];
  const cols = [];
  let x = 0;
  order.forEach((key) => {
    if (key === 'door') { cols.push({ kind: 'door', x, w: doorW, i: 0 }); x += doorW; } else { const i = Number(key[2]); cols.push({ kind: 'sidelight', x, w: slw[i], i }); x += slw[i]; }
  });
  const slabs = [], lites = [];
  const hinge = d.hinge || 'left', handleStyle = d.handle || 'lever';
  cols.forEach((c) => {
    if (c.kind === 'door') {
      if ((d.model || 'single') === 'double') {
        slabs.push(slab(c.x, 0, c.w / 2, bodyH, st, 'right', true, handleStyle, true), slab(c.x + c.w / 2, 0, c.w / 2, bodyH, st, 'left', true, handleStyle, true));
        slabs[hinge === 'right' ? slabs.length - 1 : slabs.length - 2].active = true;
      } else slabs.push(slab(c.x, 0, c.w, bodyH, st, hinge === 'left' ? 'right' : 'left', true, handleStyle, true));
    } else {
      const sh = Math.min(n(slSizes[c.i]?.height, bodyH), bodyH);
      lites.push(lite(c.x, 0, c.w, sh, 'sidelight'));
    }
  });
  if (tr === 'single') lites.push(lite(0, bodyH, W, T, 'transom'));
  else if (tr === 'each') cols.forEach((c) => lites.push(lite(c.x, bodyH, c.w, T, 'transom')));
  const dims = [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }];
  if (cols.length > 1) cols.forEach((c) => dims.push({ x1: c.x, y1: H, x2: c.x + c.w, y2: H, side: 'top', value: c.w }));
  if (T > 0) dims.push({ x1: W, y1: bodyH, x2: W, y2: H, side: 'right', value: T });
  return { w: W, h: H, slabs, lites, dims };
};

export const patioLayout = (W, H, panels, style) => {
  const st = PATIO_STYLES.includes(style) ? style : 'patio_sliding';
  const cnt = Math.max(2, Math.min(4, Number(panels) || 2));
  const pw = W / cnt;
  const slabs = Array.from({ length: cnt }, (_, i) => {
    const hs = st === 'patio_french' ? (i === 0 ? 'right' : i === 1 ? 'left' : null) : i === 1 ? 'left' : null;
    return slab(i * pw, 0, pw, H, st, hs, st !== 'patio_sliding');
  });
  const dims = [{ x1: 0, y1: 0, x2: W, y2: 0, side: 'bottom', value: W }, { x1: 0, y1: 0, x2: 0, y2: H, side: 'left', value: H }, { x1: 0, y1: H, x2: pw, y2: H, side: 'top', value: pw }];
  return { w: W, h: H, slabs, lites: [], dims };
};

// Layout da porta a partir da abertura + config efetiva. panels: nº de painéis (patio); null = automático pela largura
export const doorLayout = ({ type, width, height, door, style, panels }) => {
  const W = n(width), H = n(height);
  if (!(W > 0) || !(H > 0)) return null;
  if (type === 'ENTRY_DOOR') return entryLayout(W, H, door, style || '');
  if (type === 'PATIO_DOOR') return patioLayout(W, H, panels || autoPanels(W), style || '');
  return null;
};
